import {
  FaHospitalUser,
  FaHome,
  FaRunning,
  FaUserMd,
  FaBandAid,
  FaDumbbell,
  FaBone,
  FaWheelchair,
  FaBrain,
  FaDiagnoses,
  FaChild,
  FaBaby,
  FaUserInjured,
  FaHeartbeat,
  FaCogs,
  FaTools,
  FaSpa,
  FaUserAlt,
  FaBolt,
} from "react-icons/fa";

import {
  MdOutlineSportsHandball,
  MdOutlineSelfImprovement,
} from "react-icons/md";

import {
  GiKneeCap,
  GiFootTrip,
  GiBackPain,
  GiPotionBall,
  GiLaserBlast,
  GiRadioTower,
  GiSoundWaves,
  GiWaves,
  GiPulleyHook,
  GiSoundOn,
  GiRobotGolem,
  GiHangingSign,
} from "react-icons/gi";
import { GiBodyBalance } from "react-icons/gi";

import { TbNeedle, TbMassage } from "react-icons/tb";

import { Card } from "./ui/card";
import Header from "./Header";

const sections = [
  {
    title: "Treatment Modes & Facilities",
    description:
      "Comprehensive treatment options tailored to individual needs, supported by modern facilities.",
    items: [
      "Inpatient & Outpatient Facilities",
      "Home Care Physiotherapy",
      "Sports Injury Rehabilitation",
      "Post-Surgical Rehabilitation",
      "Chronic Pain Management",
      "Fitness & Wellness Programs",
      "Chiropractic Care",
    ],
    background: "bg-transparent",
  },
  {
    title: "Orthopedic & Musculoskeletal",
    description:
      "Expert care for bones, joints, and muscles to restore function and improve mobility.",
    items: [
      "Fracture Rehabilitation",
      "Joint Replacement Rehab",
      "Arthritis Management",
      "Back & Neck Pain Therapy",
      "Postural Correction",
      "Sports Injury Recovery",
    ],
    background:
      "py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-white relative overflow-hidden",
  },
  {
    title: "Neurological Treatments",
    description:
      "Specialized therapies designed to improve movement, balance, and coordination for neurological conditions.",
    items: [
      "Stroke Rehabilitation",
      "Parkinson's Disease Therapy",
      "Multiple Sclerosis Management",
      "Cerebral Palsy Treatment",
      "Spinal Cord Injury Rehabilitation",
      "Gait & Balance Training",
    ],
    background: "bg-transparent",
  },
  {
    title: "Certified Advanced Techniques",
    description:
      "Internationally recognized certifications ensuring the highest standards in physiotherapy treatments.",
    items: [
      "Dry Needling",
      "Kinesio Taping",
      "Mulligan Concept",
      "McKenzie Method",
      "Myofascial Release",
      "Cupping Therapy",
      "Sports Massage",
    ],
    background:
      "py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-white relative overflow-hidden",
  },
  {
    title: "Modalities Used",
    description:
      "Equipped with state-of-the-art physiotherapy tools and techniques to ensure the best outcomes for every patient.",
    items: [
      "Electrotherapy",
      "Laser Therapy (Scanning Model)",
      " Bilateral Tubal Ligation (BTL)",
      "Chattanooga Combo Unit",
      "Shortwave Diathermy (SWD)",
      "Longwave Diathermy (LWD)",
      "Reflexology",
      "Interferential Therapy (IFT)",
      "Traction (Intermittent & Static)",
      "Nerve Muscle Electrical Stimulation (NMES)",
      "Ultrasound Therapy",
      "Continuous Passive Motion (C.P.M.)",
      "TENS, Infrared Radiation",
      "Exercise Therapy",
      "Suspension Therapy",
      "Highly Advanced Gait Training",
    ],
    background: "bg-transparent",
  },
];

// Verified working & relevant icons
const itemIcons = [
  // Treatment Modes & Facilities
  FaHospitalUser, // Inpatient & Outpatient Facilities
  FaHome, // Home Care Physiotherapy
  MdOutlineSportsHandball, // Sports Injury Rehabilitation
  FaUserMd, // Post-Surgical Rehabilitation
  FaBandAid, // Chronic Pain Management
  FaDumbbell, // Fitness & Wellness Programs
  MdOutlineSelfImprovement, // Chiropractic Care

  // Orthopedic & Musculoskeletal
  FaBone, // Fracture Rehabilitation
  FaWheelchair, // Joint Replacement Rehab
  GiKneeCap, // Arthritis Management
  GiBackPain, // Back & Neck Pain Therapy
  FaUserAlt, // Postural Correction
  FaRunning, // Sports Injury Recovery

  // Neurological Treatments
  FaBrain, // Stroke Rehabilitation
  FaDiagnoses, // Parkinson's Disease Therapy
  FaChild, // Multiple Sclerosis Management
  FaBaby, // Cerebral Palsy Treatment
  FaUserInjured, // Spinal Cord Injury Rehabilitation
  GiFootTrip, // Gait & Balance Training

  // Certified Advanced Techniques
  TbNeedle, // Dry Needling
  FaBandAid, // Kinesio Taping
  GiBodyBalance, // Mulligan Concept
  MdOutlineSelfImprovement, // McKenzie Method
  TbMassage, // Myofascial Release
  GiPotionBall, // Cupping Therapy
  TbMassage, // Sports Massage

  // Modalities Used
  FaBolt, // Electrotherapy
  GiLaserBlast, // Laser Therapy
  FaCogs, // B.T.L
  FaTools, // Chattanooga Combo Unit
  GiRadioTower, // S.W.D.
  GiSoundWaves, // Longwave Diathermy
  FaSpa, // Reflexology
  GiWaves, // Interferential Therapy
  GiPulleyHook, // Traction
  FaHeartbeat, // NMES
  GiSoundOn, // Ultrasound Therapy
  GiRobotGolem, // CPM
  GiSoundWaves, // TENS, Infrared
  FaDumbbell, // Exercise Therapy
  GiHangingSign, // Suspension Therapy
  GiFootTrip, // Gait Training
];

export default function PhysiotherapyServices() {
  return (
    <>
      <Header />
      <div className="space-y-16 py-10">
        {sections.map((section, idx) => (
          <Card
            key={idx}
            className={`p-10 border shadow-2xl rounded-2xl ${section.background}`}
          >
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#204080]">
                {section.title}
              </h2>
              <p className="text-gray-700 mt-2 max-w-2xl mx-auto">
                {section.description}
              </p>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, i) => {
                const iconIndex =
                  idx === 0
                    ? i
                    : idx === 1
                    ? 7 + i
                    : idx === 2
                    ? 13 + i
                    : 19 + i;

                const IconComponent = itemIcons[iconIndex];
                return (
                  <li
                    key={i}
                    className="group relative overflow-hidden rounded-xl bg-white shadow-lg border border-gray-200 
                               p-5 flex items-center space-x-4 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#204080] to-[#2E5AAC] flex items-center justify-center group-hover:scale-110 transition">
                      <IconComponent size={20} className="text-white" />
                    </div>
                    <span className="text-gray-800 font-medium group-hover:text-[#204080] transition">
                      {item}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}
      </div>
    </>
  );
}
